import { JSX } from 'solid-js/jsx-runtime';
import { Show, For, createMemo } from 'solid-js';

import { Context } from '@bitiotic/bitiotic';
import { Persistent, RoundWinnings } from '@bitiotic/numbet';

import { DebugObj } from './DebugObj';
import { GamePlayerListener, StyledPNum } from './GamePlayerListener';
import { BetMultiplier, formatCurrency, MinusSign, PlusSign } from './Widgets';


function ReportMyChip(props: {
    ctx: Context,
    betpath: Persistent.BetSpotPath,
    payout: Persistent.PlayerBetPayout | undefined,
    singleBet: boolean,
}): JSX.Element {
    if (!props.payout) {
        return <></>;
    }
    return <>
        <Show when={props.singleBet}>
            {formatCurrency(props.payout.payout)}
            {' '}for betting
            {' '}{formatCurrency(props.payout.bet)}
            {' '}<BetMultiplier val={props.payout.multiplier} />
            {' '}on the best guess!
        </Show >
        <Show when={!props.singleBet}>
            {formatCurrency(props.payout.payout)}
            {' '}for betting
            {' '}{formatCurrency(props.payout.bet)}
            {' '}<BetMultiplier val={props.payout.multiplier} />
            {' '}on a winner.
        </Show>
    </>;
}


// Show results from a round for one player:
//
// - winning guess (or not)
//   * exact answer if winning guess
//
// - net total: positive or negative
// - grant kept/spent
//   * if all spent, not interesting to highlight grant
// - losing bets
//   * amount lost (*beyond* grant, grant lost not interesting)
//   * number of bet spots lost on
// - winning bets
//   * amount won
//   * won with "extra" (beyond grant)
function ReportMyRoundResults(props: {
    ctx: Context,
    playerPath: Persistent.PlayerPath,
    myWinnings: Readonly<RoundWinnings> | null,
    gamePlayerListener: GamePlayerListener,
}): JSX.Element {
    const nothingmuch = createMemo<boolean>(() => {
        const w = props.myWinnings;
        return (w !== null) &&
            w.excessBet === 0 &&
            w.grantRemainder === 0 &&
            w.winningGuessPayout === 0 &&
            w.betWinnings === 0;
    });

    const netmessage = createMemo<string>(() => {
        const n = props.myWinnings?.net ?? 0;
        if (n > 0) {
            return `made ${formatCurrency(n)}`;
        }
        if (n < 0) {
            return `lost ${formatCurrency(-n)}`;
        }
        return `made ${formatCurrency(0)}`;
    });

    // map from betPath to betPayout for this player
    const betPayouts = createMemo(() => {
        return Object.entries(props.myWinnings?.betPayouts ?? {});
    });

    return <li data-testid="MyRoundResults">
        <Show when={nothingmuch()}>
            <span>
                You (<StyledPNum
                    ctx={props.ctx}
                    playerPath={props.playerPath}
                    gamePlayerListener={props.gamePlayerListener} />)
                made {formatCurrency(props.myWinnings?.net ?? 0)}.
            </span>
            <ul class="list-inside marker:text-green-900 border border-green-900 ml-3 px-3 py-1">
                <li><MinusSign /> Your answer was not the best answer.</li>
                <li><MinusSign /> Your bets were not on the winning answer.</li>
            </ul>
        </Show>
        <Show when={!nothingmuch()}>
            <Show when={props.myWinnings} keyed>
                {(myWinnings) => <> {/* so internal myWinnings is definitely defined */}
                    <span>
                        You (<StyledPNum
                            ctx={props.ctx}
                            playerPath={props.playerPath}
                            gamePlayerListener={props.gamePlayerListener} />)
                        {' '}{netmessage()} from:
                    </span>

                    <ul class="list-inside marker:text-green-700 border border-green-700 ml-3 px-3 py-1">
                        <Show when={myWinnings.winningGuess}>
                            <Show when={myWinnings.winningGuessDelta === 0}>
                                <li>
                                    <div class="inline-block mx-1">
                                        <PlusSign />
                                        {formatCurrency(myWinnings.winningGuessPayout)}
                                        {' '}for answering {props.myWinnings?.winningGuess} exactly!
                                    </div>
                                </li>
                            </Show>
                            <Show when={myWinnings.winningGuessDelta !== 0}>
                                <li>
                                    <div class="inline-block mx-1">
                                        <PlusSign />
                                        {formatCurrency(myWinnings.winningGuessPayout)}
                                        {' '}for the best guess ({props.myWinnings?.winningGuess}).
                                    </div>
                                </li>
                            </Show>
                        </Show>
                        <For each={betPayouts()}>
                            {([betpath, betpayout]) =>
                                <Show when={betpayout.payout !== 0}>
                                    <li>
                                        <div class="inline-block mx-1">
                                            <PlusSign />
                                            <ReportMyChip
                                                ctx={props.ctx}
                                                betpath={Persistent.asBetSpotPath(betpath)}
                                                payout={betpayout}
                                                singleBet={betPayouts().length === 1}
                                            />
                                        </div>
                                    </li>
                                </Show>}
                        </For>
                        <Show when={myWinnings.winningBetExcess > 0}>
                            <li>
                                <div class="inline-block mx-1">
                                    <MinusSign />
                                    {formatCurrency(myWinnings.winningBetExcess)}
                                    {' '}of prior winnings spent on winning bet.
                                </div>
                            </li>
                        </Show>
                        <Show when={myWinnings.betWinnings === 0}>
                            <li>
                                <div class="inline-block mx-1">
                                    <MinusSign />
                                    No bets on the winning answer.
                                </div>
                            </li>
                        </Show>
                        <Show when={myWinnings.losingBetExcess > 0}>
                            <li>
                                <div class="inline-block mx-1">
                                    <MinusSign />
                                    {formatCurrency(myWinnings.losingBetExcess)} of prior winnings lost on
                                    <Show when={myWinnings.losingBetCount === 1}> a bad bet.</Show>
                                    <Show when={myWinnings.losingBetCount !== 1}> bad bets.</Show>
                                </div>
                            </li>
                        </Show>
                        <Show when={myWinnings.grantRemainder > 0}>
                            <li>
                                <div class="inline-block mx-1">
                                    <PlusSign />
                                    {formatCurrency(myWinnings.grantRemainder)} of grant unbet.
                                </div>
                            </li>
                        </Show>
                    </ul>
                </>}
            </Show>
        </Show >
    </li >;
}

function ReportOthersResults(props: {
    ctx: Context,
    playerPath: Persistent.PlayerPath,
    results: Readonly<Persistent.RoundResults> | null,
    gamePlayerListener: GamePlayerListener,
}): JSX.Element {

    interface PlayerWin {
        playerPath: Persistent.PlayerPath,
        playerNet: number,
        correctGuess: boolean,
    }

    const guessPhrase = createMemo(() => {
        const ct = Object.keys(props.results?.winningGuesses || {}).length;
        if (ct > 1) {
            return 'tied for best guess';
        }
        return 'had the best guess';
    });

    const otherTotals = createMemo(() => {
        const totals: PlayerWin[] = [];
        const winningPlayers = Object.keys(props.results?.playerNet || {});
        winningPlayers.forEach(playerPath => {
            if (playerPath !== props.playerPath) {
                const correctGuess = Boolean(props.results?.winningGuesses?.[playerPath]);
                totals.push({
                    playerPath: Persistent.asPlayerPath(playerPath),
                    playerNet: props.results?.playerNet[playerPath] || 0,
                    correctGuess,
                });
            }
        });

        totals.sort((t1, t2) => t2.playerNet - t1.playerNet);

        return totals;
    });

    return <>
        <For each={otherTotals()}>
            {other => {
                return <li>
                    <StyledPNum
                        ctx={props.ctx}
                        playerPath={other.playerPath}
                        gamePlayerListener={props.gamePlayerListener}
                    />
                    <Show when={other.playerNet >= 0}>
                        made {formatCurrency(other.playerNet)}
                    </Show>
                    <Show when={other.playerNet < 0}>
                        lost {formatCurrency(0 - other.playerNet)}
                    </Show>
                    <Show when={other.correctGuess}>
                        &nbsp;and {guessPhrase()}
                    </Show>
                    .
                </li>;
            }}
        </For>
    </>;
}

// A section in the UI that displays the results of a compelted Round.
export function ReportRoundResults(props: {
    ctx: Context,
    playerPath: Persistent.PlayerPath,
    myWinnings: Readonly<RoundWinnings> | null,
    results: Readonly<Persistent.RoundResults> | null,
    gamePlayerListener: GamePlayerListener,
}): JSX.Element {

    return <div class="m-3 p-3 w-fit mx-auto" data-testid="RoundResults">
        <DebugObj
            _closed={true}
            _visible={false}
            playerPath={props.playerPath}
            myWinnings={props.myWinnings}
            results={props.results}
        />

        <Show when={(props.myWinnings === null) || (props.myWinnings.net <= 0)}>
            <span>Round complete.</span>
        </Show>
        <Show when={(props.myWinnings !== null) && (props.myWinnings.net > 0)}>
            <span>Round complete!</span>
        </Show>

        <ul class="list-disc list-inside p-3 ml-3">
            <ReportMyRoundResults
                ctx={props.ctx}
                playerPath={props.playerPath}
                myWinnings={props.myWinnings}
                gamePlayerListener={props.gamePlayerListener}
            />
            <ReportOthersResults
                ctx={props.ctx}
                playerPath={props.playerPath}
                results={props.results}
                gamePlayerListener={props.gamePlayerListener}
            />
        </ul>
    </div>;
}

export function ResultsSectionHeader(props: {
    ctx: Context,
    playerPath: Persistent.PlayerPath,
    myWinnings: Readonly<RoundWinnings> | null,
    results: Readonly<Persistent.RoundResults> | null,
    gamePlayerListener: GamePlayerListener,
}): JSX.Element {
    // TODO: Make the "Results" title show "Scroll for results" when they're off the screen.
    // Look into https://primitives.solidjs.community/package/intersection-observer

    const winloss = createMemo<'won' | 'lost' | 'unknown'>(() => {
        const net = props.myWinnings?.net;
        if (net === undefined) return 'unknown';
        if (net >= 0) return 'won';
        return 'lost';
    });

    // Keep terminiology in sync with <RoundTitle>
    return <div class="flex justify-between">
        <Show when={props.results === null}>
            <div class="text-slate-400">Results</div>
        </Show>
        <Show when={props.results !== null}>
            <Show when={winloss() === 'won'}>
                <div>
                    Results - answer is {props.results?.answer} - made {formatCurrency(props.myWinnings?.net ?? 0)}
                </div>
            </Show>

            <Show when={winloss() === 'lost'}>
                <div>
                    Results - answer is {props.results?.answer} - lost {formatCurrency(0 - (props.myWinnings?.net ?? 0))}
                </div>
            </Show>
            <Show when={winloss() === 'unknown'}>
                <div>
                    Results - answer is {props.results?.answer}
                </div>
            </Show>
        </Show>
    </div>;
}


